"use client";

import React from "react";
import { Calendar, User, Clock, Share2, ArrowRight, ChevronLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";


declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};



function PostSkeleton() {
  return (
    <div data-section-id="17795" className="animate-pulse space-y-8 max-w-4xl mx-auto px-4 py-12">
      <div className="h-8 bg-muted rounded w-3/4" />
      <div className="h-4 bg-muted rounded w-1/2" />
      <div className="aspect-video bg-muted rounded-xl w-full" />
      <div className="space-y-4">
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-5/6" />
      </div>
    </div>
  );
}

function RelatedPostsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-80 bg-muted rounded-xl animate-pulse" />
      ))}
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return <PostSkeleton />;
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <h2 className="text-2xl font-bold text-destructive mb-2">Error Loading Article</h2>
        <p className="text-muted-foreground mb-6">We couldn't load the garage door guide you were looking for.</p>
        <Link to="#">
          <Button variant="outline">Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <h2 className="text-2xl font-bold mb-2">Article Not Found</h2>
        <p className="text-muted-foreground mb-6">The post you are looking for does not exist or has been moved.</p>
        <Link to="#">
          {}
          <Button className="bg-primary text-primary-foreground rounded-md px-4 py-2 font-medium">Go Back Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }) : "";

  
  const wordCount = post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <article className="max-w-4xl mx-auto px-4 py-12 md:py-20">
      
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground mb-6">
          {post.categories && post.categories.length > 0 && (
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wider">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1.5 font-medium">
            <Calendar className="w-4 h-4 text-primary" />
            {date}
          </span>
          <span className="flex items-center gap-1.5 font-medium">
            <Clock className="w-4 h-4 text-primary" />
            {readTime} min read
          </span>
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-8 leading-tight">
          {post.title}
        </h1>

        <div className="flex items-center justify-center md:justify-start gap-4 border-b border-border pb-8">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-primary/20">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-muted"><User className="w-6 h-6 text-muted-foreground" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-base font-bold text-foreground">{post.author?.name || "Steve Schaefer"}</p>
              <p className="text-xs text-primary font-bold uppercase tracking-wide">Expert Technician</p>
            </div>
          </div>
        </div>
      </header>

      
      {post.featuredImage && (
        <div className="mb-12 rounded-xl overflow-hidden shadow-md border border-border/50 bg-muted aspect-video relative">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      
      <div 
        className="prose prose-lg prose-neutral dark:prose-invert max-w-none mb-16 
        prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight prose-p:leading-relaxed prose-p:text-muted-foreground prose-img:rounded-xl prose-a:text-primary prose-strong:text-foreground"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      
      <div className="border-t border-border pt-8 mb-16">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-wrap gap-2">
            {post.tags?.map((tag) => (
              <span key={tag.id} className="text-xs font-bold px-3 py-1.5 bg-accent text-accent-foreground rounded-full hover:bg-primary hover:text-primary-foreground transition-colors cursor-default">
                #{tag.name}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold text-muted-foreground">Share this guide</span>
            {}
            <Button variant="outline" className="rounded-md bg-primary text-primary-foreground px-4 py-2 font-medium">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      
      <Card className="bg-accent/30 border-border/50 shadow-none mb-20 overflow-hidden rounded-xl">
        <CardContent className="flex flex-col md:flex-row items-center md:items-start gap-8 p-8 md:p-10">
          <Avatar className="h-24 w-24 border-4 border-background shadow-md shrink-0">
            <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
            <AvatarFallback className="bg-primary/10"><User className="w-10 h-10 text-primary" /></AvatarFallback>
          </Avatar>
          <div className="text-center md:text-left space-y-3 flex-1">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <h3 className="text-xl font-bold text-foreground">{post.author?.name || "Steve Schaefer"}</h3>
              <span className="hidden md:inline text-muted-foreground">•</span>
              <span className="text-sm text-primary font-bold uppercase tracking-wider">Owner, Garage Door Specialist</span>
            </div>
            <p className="text-muted-foreground leading-relaxed text-base">
              {post.author?.name || "Steve Schaefer"} is the owner and operator of Schaefer Garage Doors, a veteran-owned business dedicated to providing top-tier garage door services for Kingwood TX and surrounding areas. Proud U.S. Army veteran and Texas A&M graduate. Raised in Kingwood, TX, Steve brings military discipline and integrity to every job. A former champion bodybuilder, he values consistency, precision, and excellence in everything he does.
            </p>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) return <RelatedPostsSkeleton />;
  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="group flex flex-col h-full bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300">
          <Link to={post.link || "#"} className="block aspect-[3/2] overflow-hidden bg-muted relative">
            {post.featuredImage ? (
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-accent text-primary">
                <span className="text-xs font-bold uppercase tracking-widest">SchaeferGarage</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
               <span className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full shadow-md">
                  {post.categories?.[0]?.name || "Expert Guide"}
               </span>
            </div>
          </Link>
          <div className="flex flex-col flex-1 p-6">
            <div className="text-xs font-bold text-primary mb-3 flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {post.date ? new Date(post.date).toLocaleDateString() : ""}
            </div>
            <h3 className="text-lg font-bold leading-snug mb-3 group-hover:text-primary transition-colors line-clamp-2">
              <Link to={post.link || "#"}>{post.title}</Link>
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-3 mb-6 flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            <div className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6 border border-primary/10">
                  <AvatarImage src={post.author?.avatar?.["24"] || ""} />
                  <AvatarFallback className="text-[10px] bg-muted"><User className="w-3 h-3 text-muted-foreground" /></AvatarFallback>
                </Avatar>
                <span className="text-xs font-bold text-foreground">{post.author?.name || "Steve Schaefer"}</span>
              </div>
              <Link to={post.link || "#"} className="text-sm font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all">
                Read More <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}



export default function SinglePostTemplate() {
  
  
  let mainQuery: WP_Query | null = null;
  try {
    if (typeof wvcClient !== 'undefined') {
      const params = wvcClient.getCurrentWPQueryParams();
      mainQuery = new WP_Query(params);
    }
  } catch (e) {
    console.error("Failed to get WP query params", e);
  }

  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    ignore_sticky: true,
  });

  if (!mainQuery) {
    return <div className="py-20 text-center text-muted-foreground font-medium">Loading post details...</div>;
  }

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen">
      
      <div className="border-b border-border sticky top-0 z-20 bg-background/95 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="#" className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-primary transition-colors group">
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <div className="w-24 hidden md:block" /> {}
        </div>
      </div>

      
      <PostsProvider wp_query={mainQuery} className="w-full">
        <SinglePostConsumer />
      </PostsProvider>

      
      <div className="bg-accent/20 border-t border-border py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-4">
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-2">Related Articles</h2>
              <p className="text-muted-foreground font-medium">Expert advice on garage door maintenance and repair.</p>
            </div>
            <Link to="#">
              {}
              <Button variant="outline" className="hidden md:flex bg-primary text-primary-foreground rounded-md px-4 py-2 font-medium">View All Posts</Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ per_page: 3, ignore_sticky: true }}>
            <RelatedPostsConsumer />
          </PostsProvider>

          <div className="mt-12 md:hidden text-center">
             <Link to="#">
              {}
              <Button variant="outline" className="w-full bg-primary text-primary-foreground rounded-md px-4 py-2 font-medium">View All Posts</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}